import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {
  IconMapPin,
  IconMapPinCheck,
  IconMapPinOff,
  IconMapPinQuestion,
} from '@tabler/icons-react';
import type {FC, ReactNode} from 'react';
import {formatDistanceFeet} from 'src/shared/utils/geoDistance';

export type GeoStatus = 'onSite' | 'outOfRange' | 'noGps' | 'siteUnlocated';

export interface GeoStatusBannerProps {
  status: GeoStatus;
  /** Distance from the site address, in feet. Ignored for `noGps`/`siteUnlocated`. */
  distanceFeet?: number;
  /** `full` = bordered Alert; `compact` = single line for embedding in a dialog. */
  variant?: 'full' | 'compact';
  /** True while device geo capture is in flight — overrides `status` with a neutral loading render. */
  capturing?: boolean;
}

interface GeoStatusConfig {
  color: string;
  icon: ReactNode;
  label: (distanceFeet?: number) => string;
}

const CONFIG: Record<GeoStatus, GeoStatusConfig> = {
  onSite: {
    color: 'green',
    icon: <IconMapPinCheck size={18} color="var(--mantine-color-green-7)" />,
    label: distanceFeet =>
      distanceFeet !== undefined
        ? `${formatDistanceFeet(distanceFeet)} from site address`
        : 'On site',
  },
  outOfRange: {
    color: 'yellow',
    icon: <IconMapPin size={18} color="var(--mantine-color-yellow-8)" />,
    label: distanceFeet =>
      `~${distanceFeet !== undefined ? formatDistanceFeet(distanceFeet) : 'unknown distance'} away`,
  },
  noGps: {
    color: 'gray',
    icon: <IconMapPinOff size={18} color="var(--mantine-color-gray-6)" />,
    label: () => 'Location unavailable on this device',
  },
  siteUnlocated: {
    color: 'gray',
    icon: <IconMapPinQuestion size={18} color="var(--mantine-color-gray-6)" />,
    label: () => 'Site has no recorded coordinates',
  },
};

/** Domain composition over `Alert` — surfaces geo-fence status for Start/Complete. Geo never blocks. */
export const GeoStatusBanner: FC<GeoStatusBannerProps> = ({
  status,
  distanceFeet,
  variant = 'full',
  capturing = false,
}) => {
  const {color, icon, label}: GeoStatusConfig = capturing
    ? {
        color: 'gray',
        icon: <Loader size={16} color="var(--mantine-color-gray-6)" />,
        label: () => 'Getting your location…',
      }
    : CONFIG[status];
  const text = label(distanceFeet);

  if (variant === 'compact') {
    return (
      <Group gap={6} wrap="nowrap" role="status">
        {icon}
        <Text size="sm" c={color === 'gray' ? 'dimmed' : `${color}.8`}>
          {text}
        </Text>
      </Group>
    );
  }

  return (
    <Alert color={color} variant="light" icon={icon} role="status">
      {text}
    </Alert>
  );
};
